import React, { useState, useEffect } from 'react';
import Table from '../components/common/Table';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import SearchBar from '../components/common/SearchBar';
import { employeeApi } from '../api/employeeApi';
import { companyApi } from '../api/companyApi';
import { formatDateTime } from '../utils/formatDate';
import {
  ClipboardList,
  ShieldAlert,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  Check,
  X,
  MessageSquare,
  Wrench,
  Sparkles,
  Search,
  Filter
} from 'lucide-react';
import { useSocket } from '../hooks/useSocket';
import toast from 'react-hot-toast';

const RequestForms = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const { socket } = useSocket();
  
  // Rectify & Solve Modal state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customNewLimit, setCustomNewLimit] = useState('');
  const [resolutionNote, setResolutionNote] = useState('');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await employeeApi.getLimitRequests({ status: statusFilter });
      setRequests(res?.data || res || []);
    } catch (err) {
      toast.error('Failed to fetch admin requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();

    if (socket) {
      const handleLiveRequestUpdate = () => {
        fetchRequests();
      };

      socket.on('notification_received', handleLiveRequestUpdate);
      socket.on('new_notification', handleLiveRequestUpdate);
      socket.on('new_chat_message', handleLiveRequestUpdate);
      socket.on('limit_request_created', handleLiveRequestUpdate);

      return () => {
        socket.off('notification_received', handleLiveRequestUpdate);
        socket.off('new_notification', handleLiveRequestUpdate);
        socket.off('new_chat_message', handleLiveRequestUpdate);
        socket.off('limit_request_created', handleLiveRequestUpdate);
      };
    }
  }, [statusFilter, socket]);

  const handleOpenRectifyModal = (req) => {
    setSelectedRequest(req);
    setCustomNewLimit(req.requestedLimit || '');
    setResolutionNote('');
    setIsResolveModalOpen(true);
  };

  const handleApprove = async (requestId, newLimit, note) => {
    setSubmitting(true);
    try {
      await employeeApi.approveLimitRequest(requestId, { newLimit, note });
      toast.success(`Request resolved! Employee limit increased to ${newLimit}.`);
      setIsResolveModalOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.message || 'Failed to approve request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (requestId, note) => {
    setSubmitting(true);
    try {
      await employeeApi.rejectLimitRequest(requestId, { note });
      toast.error('Admin request rejected with response note');
      setIsResolveModalOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.message || 'Failed to reject request');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter requests based on search query
  const filteredRequests = requests.filter((r) => {
    const term = search.toLowerCase();
    const companyName = (r.companyId?.name || '').toLowerCase();
    const adminName = (r.adminId?.name || '').toLowerCase();
    const reason = (r.reason || '').toLowerCase();
    return companyName.includes(term) || adminName.includes(term) || reason.includes(term);
  });

  // Calculate statistics
  const totalCount = requests.length;
  const pendingCount = requests.filter((r) => r.status === 'Pending').length;
  const approvedCount = requests.filter((r) => r.status === 'Approved').length;
  const rejectedCount = requests.filter((r) => r.status === 'Rejected').length;

  const columns = [
    {
      header: 'Company Tenant',
      accessor: 'companyId',
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center font-bold text-teal-700 font-display">
            {row.companyId?.name ? row.companyId.name.substring(0, 2).toUpperCase() : 'CO'}
          </div>
          <div>
            <p className="font-extrabold text-slate-900 font-display text-xs">
              {row.companyId?.name || 'Company Tenant'}
            </p>
            <p className="text-[11px] text-slate-500 font-medium">{row.companyId?.industry || 'Multi-tenant'}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Requesting Admin',
      accessor: 'adminId',
      cell: (row) => (
        <div>
          <p className="font-bold text-slate-800 text-xs">{row.adminId?.name || 'Admin User'}</p>
          <p className="text-[11px] text-slate-400 font-medium">{row.adminId?.email || 'admin@tenant.com'}</p>
        </div>
      )
    },
    {
      header: 'Limit & Quota Request',
      accessor: 'requestedLimit',
      cell: (row) => (
        <div className="flex items-center space-x-2">
          <span className="text-xs font-semibold text-slate-500 line-through">
            {row.companyId?.employeeLimit || 50} Max
          </span>
          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200/60 font-mono">
            ➔ {row.requestedLimit} Requested
          </span>
        </div>
      )
    },
    {
      header: 'Reason / Form Details',
      accessor: 'reason',
      cell: (row) => (
        <p className="text-xs text-slate-600 max-w-xs truncate" title={row.reason}>
          {row.reason || 'No description provided.'}
        </p>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => {
        let variant = 'pending';
        if (row.status === 'Approved') variant = 'success';
        if (row.status === 'Rejected') variant = 'suspended';
        return <Badge variant={variant}>{row.status}</Badge>;
      }
    },
    {
      header: 'Submitted Date',
      accessor: 'createdAt',
      cell: (row) => <span className="text-xs text-slate-400">{formatDateTime(row.createdAt)}</span>
    },
    {
      header: 'Action & Rectification',
      accessor: '_id',
      cell: (row) => (
        <button
          onClick={() => handleOpenRectifyModal(row)}
          className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition shadow-sm ${
            row.status === 'Pending'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-emerald-600/20 hover:-translate-y-0.5'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}
        >
          <Wrench className="w-3.5 h-3.5" />
          <span>{row.status === 'Pending' ? 'Rectify & Solve' : 'View Resolution'}</span>
        </button>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fadeIn pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 rounded-2xl bg-white/90 backdrop-blur-xl border border-slate-200/80 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.03)]">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight font-display">
              Request Forms Desk
            </h1>
            <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-teal-50 border border-teal-200/60 text-[10px] font-extrabold text-teal-700 uppercase">
              <Sparkles className="w-3 h-3 text-teal-600" />
              Incoming Admin Requests
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Receive, review, and rectify incoming company admin requests and quota forms.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-slate-200/80 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider">Total Form Submissions</span>
            <div className="p-2 rounded-xl bg-teal-50 text-teal-700">
              <ClipboardList className="w-4 h-4" />
            </div>
          </div>
          <h3 className="text-3xl font-black text-slate-900 mt-3 font-display">{totalCount}</h3>
        </div>

        <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-amber-200/80 p-5 shadow-sm bg-gradient-to-br from-amber-50/40 to-transparent">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-extrabold text-amber-700 uppercase tracking-wider">Pending Rectification</span>
            <div className="p-2 rounded-xl bg-amber-100 text-amber-800">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <h3 className="text-3xl font-black text-amber-900 mt-3 font-display">{pendingCount}</h3>
        </div>

        <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-emerald-200/80 p-5 shadow-sm bg-gradient-to-br from-emerald-50/40 to-transparent">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-extrabold text-emerald-700 uppercase tracking-wider">Approved & Solved</span>
            <div className="p-2 rounded-xl bg-emerald-100 text-emerald-800">
              <CheckCircle className="w-4 h-4" />
            </div>
          </div>
          <h3 className="text-3xl font-black text-emerald-900 mt-3 font-display">{approvedCount}</h3>
        </div>

        <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-rose-200/80 p-5 shadow-sm bg-gradient-to-br from-rose-50/40 to-transparent">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-extrabold text-rose-700 uppercase tracking-wider">Rejected Requests</span>
            <div className="p-2 rounded-xl bg-rose-100 text-rose-800">
              <XCircle className="w-4 h-4" />
            </div>
          </div>
          <h3 className="text-3xl font-black text-rose-900 mt-3 font-display">{rejectedCount}</h3>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-white/90 backdrop-blur-xl border border-slate-200/80 shadow-sm">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by company name, admin, or reason..."
          className="w-full sm:w-80"
        />

        <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 font-semibold focus:outline-none focus:border-teal-500 transition"
          >
            <option value="">All Form Statuses</option>
            <option value="Pending">Pending Action</option>
            <option value="Approved">Approved / Solved</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Main Request Form Table */}
      {loading ? (
        <div className="py-16 text-center text-xs text-slate-400 font-medium">Loading request submissions...</div>
      ) : filteredRequests.length === 0 ? (
        <div className="py-16 text-center text-xs text-slate-400 font-medium">No admin requests matching your criteria.</div>
      ) : (
        <Table columns={columns} data={filteredRequests} />
      )}

      {/* Rectify & Solve Modal */}
      {selectedRequest && (
        <Modal
          isOpen={isResolveModalOpen}
          onClose={() => setIsResolveModalOpen(false)}
          title="Rectify & Solve Admin Request"
          maxWidth="max-w-lg"
        >
          <div className="space-y-5">
            {/* Details Box */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-900 font-display">
                  {selectedRequest.companyId?.name || 'Company Tenant'}
                </span>
                <Badge variant={selectedRequest.status === 'Approved' ? 'success' : selectedRequest.status === 'Rejected' ? 'suspended' : 'pending'}>
                  {selectedRequest.status}
                </Badge>
              </div>

              <div className="text-xs text-slate-500">
                Requested by: <span className="font-semibold text-slate-800">{selectedRequest.adminId?.name}</span> ({selectedRequest.adminId?.email})
              </div>

              <div className="p-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 leading-relaxed italic">
                "{selectedRequest.reason || 'No description provided.'}"
              </div>
            </div>

            {/* Quotas & Action Input */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Approved Employee Limit Quota
              </label>
              <input
                type="number"
                value={customNewLimit}
                onChange={(e) => setCustomNewLimit(e.target.value)}
                placeholder="Enter approved limit (e.g. 100)"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm font-mono font-bold text-slate-900 focus:outline-none focus:border-teal-500 transition"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Resolution Response Note (Sent to Admin)
              </label>
              <textarea
                rows={3}
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                placeholder="Write resolution notes to rectify this request..."
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs text-slate-900 focus:outline-none focus:border-teal-500 transition"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => handleReject(selectedRequest._id, resolutionNote)}
                disabled={submitting}
                className="px-4 py-2.5 rounded-xl bg-rose-50 text-rose-700 hover:bg-rose-100 font-bold text-xs transition"
              >
                Reject Request
              </button>

              <button
                type="button"
                onClick={() => handleApprove(selectedRequest._id, Number(customNewLimit), resolutionNote)}
                disabled={submitting || !customNewLimit}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/30 transition hover:-translate-y-0.5 disabled:opacity-50"
              >
                {submitting ? 'Solving...' : 'Approve & Solve Request'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RequestForms;
